import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { AssigneeSelector } from "./assignee-selector";
import {
  ChannelBadge,
  PriorityBadge,
  StatusBadge,
} from "./badges/conversation-badges";
import { ConversationDetailsSection } from "./conversation-details-section";
import { ConversationTicketFieldTitle } from "./conversation-ticket-field-title";

export interface ConversationDetailsPanelProps {
  conversation: ConversationListItemResponse;
}

/**
 * Right panel component that displays conversation details.
 * Shows overview (status, priority) and customer info placeholders.
 */
export const ConversationDetailsPanel = memo(function ConversationDetailsPanel({
  conversation,
}: ConversationDetailsPanelProps) {
  return (
    <Flex dir="column" flex={1} gap="lg" overflowY="auto" p="xl">
      {/* Overview Section */}
      <ConversationOverviewSection conversation={conversation} />

      {/* Customer Info Section - Placeholder */}
      <CustomerInfoSection />

      {/* Orders Section - Placeholder */}
      <OrdersSection />
    </Flex>
  );
});

interface ConversationOverviewSectionProps {
  conversation: ConversationListItemResponse;
}

const ConversationOverviewSection = memo(function ConversationOverviewSection({
  conversation,
}: ConversationOverviewSectionProps) {
  return (
    <ConversationDetailsSection
      localStorageKey="redo.support-v2.conversation-details.ticket.collapsed"
      title="Ticket"
    >
      <AssigneeSelector
        conversationId={conversation.id}
        mongoAssigneeId={conversation.mongoAssigneeId}
      />
      {conversation.priority && (
        <Flex align="center" gap="md" justify="space-between">
          <ConversationTicketFieldTitle>Priority</ConversationTicketFieldTitle>
          <PriorityBadge priority={conversation.priority} />
        </Flex>
      )}
      <Flex align="center" gap="md" justify="space-between">
        <ConversationTicketFieldTitle>Status</ConversationTicketFieldTitle>
        <StatusBadge status={conversation.status} />
      </Flex>
      <Flex align="center" gap="md" justify="space-between">
        <ConversationTicketFieldTitle>Channel</ConversationTicketFieldTitle>
        {conversation.channels.map((channel) => (
          <ChannelBadge channel={channel} key={channel} size="sm" />
        ))}
      </Flex>
      <Flex align="center" gap="md" justify="space-between">
        <ConversationTicketFieldTitle>Created At</ConversationTicketFieldTitle>
        <Text fontSize="xs" textColor="tertiary">
          {conversation.lastCustomerMessageAt
            ? conversation.lastCustomerMessageAt.toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })
            : "-"}
        </Text>
      </Flex>
      <Flex align="center" gap="md" justify="space-between">
        <ConversationTicketFieldTitle>
          Last Response
        </ConversationTicketFieldTitle>
        <Text fontSize="xs" textColor="tertiary">
          {conversation.lastCustomerMessageAt
            ? conversation.lastCustomerMessageAt.toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })
            : "-"}
        </Text>
      </Flex>
      <Flex align="center" gap="md" justify="space-between">
        <ConversationTicketFieldTitle>Tags</ConversationTicketFieldTitle>
        <Text fontSize="xs" textColor="tertiary">
          Tags will be displayed here
        </Text>
      </Flex>
    </ConversationDetailsSection>
  );
});

const CustomerInfoSection = memo(function CustomerInfoSection() {
  return (
    <ConversationDetailsSection
      localStorageKey="redo.support-v2.conversation-details.customer.collapsed"
      title="Customer"
    >
      <Text fontSize="xs" textColor="tertiary">
        Customer info will be displayed here
      </Text>
    </ConversationDetailsSection>
  );
});

const OrdersSection = memo(function OrdersSection() {
  return (
    <ConversationDetailsSection
      localStorageKey="redo.support-v2.conversation-details.orders.collapsed"
      title="Orders"
    >
      <Text fontSize="xs" textColor="tertiary">
        Orders will be displayed here
      </Text>
    </ConversationDetailsSection>
  );
});
